import classnames from 'classnames'
import styles from './ProductPageUiLeftSide.module.scss'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { DOC_TYPES, PRODUCTS_SLUG } from '@/data'
import Link from '@/components/Link/Link'
import SanityImage from '@/components/SanityImage/SanityImage'
import useStore from '@/store'
import gsap from 'gsap'
import { ZOOM_IMAGE_ID } from '@/components/ZoomImage/ZoomImage'
import { Flip } from 'gsap/dist/Flip'

gsap.registerPlugin(Flip)

export const DETAIL_IMAGE_ID = 'detailImageId-69'
export const DETAIL_IMAGE_BREAKPOINTS = {
  tablet: {
    width: 3000,
  },
  xs: {
    width: 1000,
  },
}

const ProductPageUiLeftSide = forwardRef(({ className, productData }, ref) => {
  const textLinesRefs = useRef({
    description: null,
    detailTitle: null,
    downloadSpecSheetLabel: null,
    finishesLabel: null,
    closeButton: null,
    descriptionItalic: null,
  })
  const $styleLabelRef = useRef(null)
  const productPageActiveImage = useStore(state => state.productPageActiveImage)
  const [styleLabel, setStyleLabel] = useState(null)
  const setZoomImageOpen = useStore(state => state.setZoomImageOpen)
  const zoomImageOpen = useStore(state => state.zoomImageOpen)
  const $detailImageContainerRef = useRef(null)
  const zoomImageHasBeenOpen = useRef(false)
  const setProductGalleryImagesOpen = useStore(state => state.setProductGalleryImagesOpen)
  const animateZoomImage = (animatingIn = true) => {
    if (!$detailImageContainerRef.current) return

    gsap.killTweensOf($detailImageContainerRef.current)

    gsap.to($detailImageContainerRef.current, {
      '--y': animatingIn ? '0%' : '100%',
      duration: animatingIn ? 0.6 : 0.4,
      delay: animatingIn ? 0.25 : 0,
      ease: 'Power3.easeOut',
    })
  }

  const animateImageToPosition = () => {
    const $elToFitFrom = document.getElementById(ZOOM_IMAGE_ID)

    if (!$elToFitFrom || !$detailImageContainerRef.current) return

    const state = Flip.getState($detailImageContainerRef.current)

    Flip.fit($detailImageContainerRef.current, $elToFitFrom, {
      scale: true,
    })

    Flip.to(state, {
      duration: 0.6,
      ease: 'Power4.easeOut',
      scale: true,
    })
  }

  useEffect(() => {
    if (zoomImageOpen) {
      zoomImageHasBeenOpen.current = true
    }

    if (zoomImageHasBeenOpen.current) {
      if (!zoomImageOpen) {
        animateImageToPosition()
      }
    }
  }, [zoomImageOpen])

  useEffect(() => {
    if (productPageActiveImage) {
      let title = productData.title

      if (productPageActiveImage?.label) {
        title = productPageActiveImage?.label
      }

      setStyleLabel(title)
    }
  }, [productPageActiveImage, productData])

  useImperativeHandle(ref, () => ({
    animateIn: () => {
      Object.values(textLinesRefs.current).forEach(($ref, i) => {
        const key = Object.keys(textLinesRefs.current)[i]

        let config = {}

        if (key === 'description') {
          config = {
            stagger: 0,
          }
        }

        if ($ref) $ref.animate(config)
      })

      if ($styleLabelRef.current) {
        gsap.killTweensOf($styleLabelRef.current)
        gsap.to($styleLabelRef.current, {
          autoAlpha: 1,
          duration: 0.6,
        })
      }

      animateZoomImage(true)
    },
    animateOut: () => {
      Object.values(textLinesRefs.current).forEach(($ref, i) => {
        const key = Object.keys(textLinesRefs.current)[i]

        let config = {}

        if (key === 'description') {
          config = {
            stagger: 0,
          }
        }

        if ($ref) $ref.animateOut(config)
      })

      if ($styleLabelRef.current) {
        gsap.killTweensOf($styleLabelRef.current)
        gsap.to($styleLabelRef.current, {
          autoAlpha: 0,
          duration: 0.35,
        })
      }

      animateZoomImage(false)
    },
    reset: () => {
      Object.values(textLinesRefs.current).forEach($ref => {
        if ($ref) $ref.reset()
      })

      animateZoomImage(false)
    },
  }))

  return (
    <div className={classnames(styles.ProductPageUiLeftSide, className)}>
      {/* Close button */}
      <Link
        link={{
          linkType: 'internal',
          link: {
            slug: PRODUCTS_SLUG,
            _type: DOC_TYPES.PAGE,
          },
        }}
        className={styles.closeButtonContainer}
      >
        <AnimatedTextLines
          querySelector="span"
          ref={ref => {
            textLinesRefs.current.closeButton = ref
          }}
          autoAnimate={false}
          className={classnames(styles.closeButton__lines, styles.textLines)}
        >
          <span className={classnames(styles.closeButton__text, styles.textLinesValue)}>Close</span>
        </AnimatedTextLines>
      </Link>

      {/* Description */}
      <div className={styles.descriptionContainer}>
        <div className={styles.descriptionContainer__italic}>
          <AnimatedTextLines
            querySelector="span"
            ref={ref => {
              textLinesRefs.current.descriptionItalic = ref
            }}
            autoAnimate={false}
            className={classnames(styles.descriptionItalic__lines, styles.textLines)}
          >
            <span className={classnames(styles.descriptionItalic, styles.textLinesValue)}>i.</span>
          </AnimatedTextLines>
        </div>
        <AnimatedTextLines
          querySelector="span"
          ref={ref => {
            textLinesRefs.current.description = ref
          }}
          autoAnimate={false}
          className={classnames(styles.description__lines, styles.textLines)}
        >
          <span className={classnames(styles.description, styles.textLinesValue)}>
            {productData?.productData?.description}
          </span>
        </AnimatedTextLines>
      </div>

      {/* Style Label */}
      <div className={styles.styleLabelContainer}>
        <span
          className={styles.style__label}
          ref={$styleLabelRef}
        >
          <span className={styles.style__label__number}>ii.</span>
          {styleLabel || ''}
        </span>
      </div>

      <div className={styles.bottom}>
        {productData?.productData?.zoomImage && (
          <div className={classnames(styles.detailContainer, { [styles.zoomImageOpen]: zoomImageOpen })}>
            <div className={styles.detailTitleContainer}>
              <AnimatedTextLines
                querySelector="span"
                ref={ref => {
                  textLinesRefs.current.detailTitle = ref
                }}
                autoAnimate={false}
                className={classnames(styles.detailTitle__lines, styles.textLines)}
              >
                <span className={classnames(styles.detailTitle, styles.textLinesValue)}>(Detail)</span>
              </AnimatedTextLines>
            </div>

            <button
              id={DETAIL_IMAGE_ID}
              className={styles.detailImageContainer}
              onClick={() => {
                setZoomImageOpen(true)
              }}
              ref={$detailImageContainerRef}
              aria-label="Open zoom image"
            >
              <SanityImage
                image={productData?.productData?.zoomImage}
                breakpoints={DETAIL_IMAGE_BREAKPOINTS}
                disableLoadedOpacity={true}
                className={classnames(styles.detailImage, styles.large)}
              />
              <SanityImage
                image={productData?.productData?.zoomImage}
                breakpoints={{
                  tablet: {
                    width: 400,
                  },
                  xs: {
                    width: 200,
                  },
                }}
                disableLoadedOpacity={true}
                className={classnames(styles.detailImage, styles.small)}
                onLoad={e => {
                  if (e?.target) {
                    gsap.to(e.target, {
                      opacity: 1,
                      duration: 0.6,
                    })
                  }
                }}
              />
            </button>
          </div>
        )}

        {productData?.productData?.specSheet?.url && (
          <div className={styles.downloadSpecSheetLabelContainer}>
            <Link
              className={classnames(styles.downloadSpecSheetLabel, styles.textLinesValue)}
              link={{
                linkType: 'external',
                link: productData?.productData?.specSheet?.url,
              }}
            >
              <AnimatedTextLines
                querySelector="span"
                ref={ref => {
                  textLinesRefs.current.downloadSpecSheetLabel = ref
                }}
                autoAnimate={false}
                className={classnames(styles.downloadSpecSheetLabel__lines, styles.textLines)}
              >
                <span className={classnames(styles.downloadSpecSheetLabel, styles.textLinesValue)}>
                  Download Spec Sheet <DownIcon />
                </span>
              </AnimatedTextLines>
            </Link>
          </div>
        )}

        {/* Finishes Label */}
        {productData?.productData?.galleryImages?.length > 0 && (
          <button
            onClick={() => {
              setProductGalleryImagesOpen(true)
            }}
            className={styles.finishesLabelContainer}
          >
            <AnimatedTextLines
              querySelector="span"
              ref={ref => {
                textLinesRefs.current.finishesLabel = ref
              }}
              autoAnimate={false}
              className={classnames(styles.finishesLabel__lines, styles.textLines)}
            >
              <span className={classnames(styles.finishesLabel, styles.textLinesValue)}>Finishes</span>
            </AnimatedTextLines>
          </button>
        )}
      </div>
    </div>
  )
})

export const DownIcon = () => {
  return (
    <svg
      className={styles.downIcon}
      viewBox="0 0 9 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 4.89304L4.9007 8.73757M4.9007 8.73757L8.8014 4.89304M4.9007 8.73757V0"
        stroke="currentColor"
        strokeWidth="0.468084"
      />
    </svg>
  )
}

ProductPageUiLeftSide.displayName = 'ProductPageUiLeftSide'

export default ProductPageUiLeftSide
